import type { Segment, MeetingSegment } from './domain.js';

/** Provider input describes a shared microphone, never an invented individual speaker. */
export function isHumanMeetingSegment(segment: Pick<MeetingSegment, 'userId' | 'responseId' | 'speaker' | 'fragment'>): boolean {
  return !!segment.userId || (!segment.responseId && segment.speaker === 'Shared microphone' && !!segment.fragment);
}

export const TRANSCRIPT_FRAGMENT_GAP_S = 1;

/** Raw row bounds stay available to controls and feedback; display text never
 * replaces the append-only transcript or its GET-after/SSE cursor. */
export function transcriptGroups<T extends Segment>(segments: readonly T[]): Array<{ segment: T; firstIndex: number; lastIndex: number }> {
  const groups: Array<{ segment: T; firstIndex: number; lastIndex: number }> = [];
  segments.forEach((segment, index) => {
    const previous = groups.at(-1);
    const before = previous?.segment.fragment;
    const next = segment.fragment;
    if (previous && before && next && before.streamId === next.streamId && before.utteranceId === next.utteranceId
      && next.index === (before.lastIndex ?? before.index) + 1 && previous.segment.speaker === segment.speaker
      && segment.start - previous.segment.end <= TRANSCRIPT_FRAGMENT_GAP_S) {
      previous.segment = { ...previous.segment, start: Math.min(previous.segment.start, segment.start),
        end: Math.max(previous.segment.end, segment.end), text: previous.segment.text + segment.text,
        fragment: { ...before, lastIndex: next.lastIndex ?? next.index } };
      previous.lastIndex = index;
    } else {
      groups.push({ segment: { ...segment }, firstIndex: index, lastIndex: index });
    }
  });
  return groups;
}

/** Concatenate only explicitly related, adjacent provider deltas. No spaces or
 * trimming: a word may cross any delta boundary, including a whitespace delta. */
export function coalesceTranscriptFragments<T extends Segment>(segments: readonly T[]): T[] {
  return transcriptGroups(segments).map(group => group.segment);
}
